// Composite data source: routes each metric to the REST adapter or the mock,
// per the capability map. Flip a flag to true once the backend endpoint ships
// — no UI change. On a real-endpoint absence (NotImplemented / 404 / 501) the
// composite degrades to mock and flags `mocked: true`; other errors surface.

import axios from 'axios';
import {
  ByDimensionResponse,
  ByUserResponse,
  CallDetail,
  CallStatsFilters,
  CallsPage,
  CallsQuery,
  ConnectivityResponse,
  FailuresResponse,
  PairSummary,
  QualityDimension,
  Scores,
  SummaryResponse,
} from '../callStatsTypes';
import { CallStatsDataSource } from './CallStatsDataSource';
import { MockCallStatsDataSource } from './mockCallStatsDataSource';
import {
  NotImplementedError,
  RestCallStatsDataSource,
} from './restCallStatsDataSource';

// Per-metric backend availability. Update as endpoints (docs/call-stats-dashboard.md) ship.
// All endpoints now exist (OI-1574). Quality/connectivity read empty until the
// hub POSTs call-quality summaries on hangup and real calls populate ICE types;
// flip any back to false to restore the mock for a fuller-looking demo.
export const CALL_STATS_REAL = {
  getSummary: true,
  getByUser: true,
  getPair: true,
  getFailures: true,
  getCalls: true,
  getScores: true,
  getByDimension: true,
  getConnectivity: true,
  getCallDetail: true,
} as const;

export type CallStatsMethod = keyof typeof CALL_STATS_REAL;

export interface Resolved<T> {
  data: T;
  mocked: boolean;
}

function isAbsenceError(err: unknown, degradeOn404 = true): boolean {
  if (err instanceof NotImplementedError) return true;
  if (axios.isAxiosError(err)) {
    const s = err.response?.status;
    // Only a real "endpoint absent" signal degrades to mock. A missing status (network/timeout/CORS/offline) is a genuine failure — must surface as PANEL_FAILURE, not fabricated data on a health dashboard.
    return s === 501 || (degradeOn404 && s === 404);
  }
  return false;
}

export class CompositeCallStatsDataSource {
  constructor(
    private readonly real: CallStatsDataSource = new RestCallStatsDataSource(),
    private readonly mock: CallStatsDataSource = new MockCallStatsDataSource(),
  ) {}

  private async resolve<T>(
    method: CallStatsMethod,
    realFn: () => Promise<T>,
    mockFn: () => Promise<T>,
    // When false, a 404 is treated as real "no data for this id" and surfaces
    // rather than degrading to fabricated mock (only the endpoint being absent
    // — 501 / NotImplemented — degrades). Used by getCallDetail: a 404 there
    // means the call has no quality row, not that the endpoint is missing.
    degradeOn404 = true,
  ): Promise<Resolved<T>> {
    if (!CALL_STATS_REAL[method]) {
      return { data: await mockFn(), mocked: true };
    }
    try {
      return { data: await realFn(), mocked: false };
    } catch (err) {
      if (isAbsenceError(err, degradeOn404)) {
        return { data: await mockFn(), mocked: true };
      }
      throw err;
    }
  }

  getSummary(f: CallStatsFilters): Promise<Resolved<SummaryResponse>> {
    return this.resolve(
      'getSummary',
      () => this.real.getSummary(f),
      () => this.mock.getSummary(f),
    );
  }

  getByUser(
    f: CallStatsFilters,
    limit?: number,
  ): Promise<Resolved<ByUserResponse>> {
    return this.resolve(
      'getByUser',
      () => this.real.getByUser(f, limit),
      () => this.mock.getByUser(f, limit),
    );
  }

  getPair(f: CallStatsFilters): Promise<Resolved<PairSummary>> {
    return this.resolve(
      'getPair',
      () => this.real.getPair(f),
      () => this.mock.getPair(f),
    );
  }

  getFailures(f: CallStatsFilters): Promise<Resolved<FailuresResponse>> {
    return this.resolve(
      'getFailures',
      () => this.real.getFailures(f),
      () => this.mock.getFailures(f),
    );
  }

  getCalls(q: CallsQuery): Promise<Resolved<CallsPage>> {
    return this.resolve(
      'getCalls',
      () => this.real.getCalls(q),
      () => this.mock.getCalls(q),
    );
  }

  getScores(f: CallStatsFilters): Promise<Resolved<Scores>> {
    return this.resolve(
      'getScores',
      () => this.real.getScores(f),
      () => this.mock.getScores(f),
    );
  }

  getByDimension(
    f: CallStatsFilters,
    dimension: QualityDimension,
  ): Promise<Resolved<ByDimensionResponse>> {
    return this.resolve(
      'getByDimension',
      () => this.real.getByDimension(f, dimension),
      () => this.mock.getByDimension(f, dimension),
    );
  }

  getConnectivity(
    f: CallStatsFilters,
  ): Promise<Resolved<ConnectivityResponse>> {
    return this.resolve(
      'getConnectivity',
      () => this.real.getConnectivity(f),
      () => this.mock.getConnectivity(f),
    );
  }

  getCallDetail(callId: string): Promise<Resolved<CallDetail>> {
    return this.resolve(
      'getCallDetail',
      () => this.real.getCallDetail(callId),
      () => this.mock.getCallDetail(callId),
      false, // a 404 = this call has no quality row; surface it, don't mock it
    );
  }
}

export const callStatsDataSource = new CompositeCallStatsDataSource();
